import { I18n } from "@assets/strings";
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useNotifications } from "@domain/notification/hooks";
import { useChangeEffect, useIsMobile } from "@utils/hooks";
import { TOP_BAR_OFFSET, TOP_BAR_OFFSET_MOBILE } from "@ui/styles/utils";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Secondary } from "@ui/components/containers/textBlocks";
import { DeleteIcon, EditIcon, EyeIcon, NotificationIcon } from "@ui/components/icons";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { Menu } from "@ui/components/menus/menu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { NotificationView } from "./notificationView";

export interface NotificationMenuProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	onChange: (open: boolean) => void;
}

export const NotificationMenu: React.FC<NotificationMenuProps> = ({ onChange, ...htmlProps }) => {
	const { result: notifications, loading, moreLoading, fetch, fetchMore, extras } = useNotifications();
	const contentRef = useRef<HTMLDivElement>(null);
	const { notificationService } = useServices();
	const unreadNotifications = extras?.unread;
	const isMobile = useIsMobile();
	const location = useLocation();
	const refreshOnChangeRef = useRef<NodeJS.Timeout>(null);

	useChangeEffect(() => {
		if (refreshOnChangeRef.current) {
			clearTimeout(refreshOnChangeRef.current);
		}
		refreshOnChangeRef.current = setTimeout(() => {
			void fetch();
		}, 2000);
	}, [location.pathname]);

	useEffect(() => {
		const refreshInterval = setInterval(
			() => {
				if (refreshOnChangeRef.current) {
					clearTimeout(refreshOnChangeRef.current);
				}
				void fetch();
			},
			2 * 60 * 1000
		);
		return () => clearInterval(refreshInterval);
	}, []);

	return (
		<StyledMenu
			fixed="right"
			menuContentStyle={{ padding: 0, whiteSpace: "initial", height: "100%" }}
			onOpenChange={(opened) => {
				if (opened && refreshOnChangeRef.current) {
					clearTimeout(refreshOnChangeRef.current);
					void fetch();
				} else {
					notificationService.readAll();
				}
				onChange(opened);
			}}
			renderMenuContent={(close) => (
				<Container ref={contentRef} {...htmlProps}>
					<Content>
						<div>
							<Title>
								{I18n.t("notification.title")}
								<KebabMenu closeOnSelect>
									<Stack gap={20}>
										<Link to={Path.Settings}>
											<MenuRow onClick={close}>
												<EditIcon />
												<span>{I18n.t("profile.menu.settings")}</span>
											</MenuRow>
										</Link>
										<MenuRow onClick={() => notificationService.readAll()}>
											<EyeIcon />
											<span>{I18n.t("notification.read_all")}</span>
										</MenuRow>
									</Stack>
								</KebabMenu>
								{isMobile && (
									<Close onClick={close}>
										<DeleteIcon />
									</Close>
								)}
							</Title>
							{notifications.length > 0 ? (
								<InfiniteScroll onEndReached={fetchMore} scrollingElement={contentRef} loading={loading || moreLoading}>
									{notifications.map((notification) => (
										<NotificationView key={notification.id} notification={notification} close={close} />
									))}
								</InfiniteScroll>
							) : (
								<Empty>{I18n.t("notification.empty")}</Empty>
							)}
							{(loading || moreLoading) && <ClipLoader />}
						</div>
					</Content>
				</Container>
			)}
		>
			<Wrapper>
				<NotificationIcon />
				{unreadNotifications ? (
					<RedCircle length={`${unreadNotifications}`.length}>
						{unreadNotifications > 999 ? 999 : unreadNotifications}
					</RedCircle>
				) : null}
			</Wrapper>
		</StyledMenu>
	);
};

const StyledMenu = styled(Menu)`
	svg {
		flex-shrink: 0;
	}
`;

const Wrapper = styled.div`
	position: relative;
	width: 44px;
	height: 44px;
	border-radius: 22px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	display: flex;
	align-items: center;
	justify-content: center;

	svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;
const Container = styled.div`
	width: 404px;
	max-width: 100vw;
	position: relative;
`;

const Content = styled.div`
	overflow: auto;
	max-height: calc(100vh - ${TOP_BAR_OFFSET}px);
	@media (max-width: 900px) {
		max-height: calc(100vh - ${TOP_BAR_OFFSET_MOBILE}px);
	}

	> div {
		width: 404px;
		max-width: 100vw;
		overflow: hidden;
		padding-bottom: 20px;
	}
`;

const Title = styled(PrimaryMedium)`
	${({ theme }) => theme.textStyles.subtitle};
	margin: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const RedCircle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "length",
})<{ length: number }>`
	${({ length }) =>
		length < 2
			? css`
					width: 14px;
					height: 14px;
				`
			: length < 3
				? css`
						width: 16px;
						height: 16px;
					`
				: css`
						width: 22px;
						height: 22px;
					`}
	position: absolute;
	right: 0;
	top: 0;
	border-radius: 11px;
	background-color: ${({ theme }) => theme.colors.red};
	color: ${({ theme }) => theme.colors.white};
	font-size: 0.6875rem;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Close = styled.div`
	cursor: pointer;
	padding: 5px;
`;

const Empty = styled(Secondary)`
	margin: 20px;
`;
